// =============================================================================
// features/sales/campaigns/detail/tabs/SettingsTab.tsx — Aba "Configuração":
// regras editáveis conforme o estado da campanha (doc 26 §6.7/§6.9).
//
// Campos editáveis dependem do status (ver useCampaignSettingsForm.ts, que
// espelha `allowedPatchFields` do backend). Em `active`, salvar mostra o
// impacto antes de confirmar (doc 26 §6.7 "Alterações em campanha ativa
// devem mostrar impacto antes da confirmação").
//
// "Preview do plano de distribuição (dry-run de F35-S06)": a rota
// `GET /:id/distribution/preview` citada no cabeçalho de
// apps/api/src/modules/sales/campaigns/distribution/service.ts NÃO existe
// ainda em `campaigns/routes.ts` — não há endpoint HTTP para este slot
// consumir (`apps/api/**` fora de `files_allowed`, e nenhuma rota registrada
// hoje). Em vez de simular o cálculo no cliente (proibido — o motor de
// distribuição tem regras de janela/bloqueio/pool que só o backend conhece),
// esta seção fica com um estado "Indisponível" explícito.
// =============================================================================

import type { Campaign } from '@elemento/shared-schemas';
import * as React from 'react';

import { Button } from '../../../../../components/ui/Button';
import { useToast } from '../../../../../components/ui/Toast';
import { useUpdateCampaign } from '../../../../../hooks/sales/useCampaignDetail';
import { ApiError } from '../../../../../lib/api';
import { ImpactConfirmDialog } from '../components/ImpactConfirmDialog';
import { SettingsCadenceFieldset } from '../components/SettingsCadenceFieldset';
import { SettingsIdentityFieldset } from '../components/SettingsIdentityFieldset';
import { useCampaignSettingsForm } from '../useCampaignSettingsForm';

export interface SettingsTabProps {
  campaign: Campaign;
  canManage: boolean;
}

export function SettingsTab({ campaign, canManage }: SettingsTabProps): React.JSX.Element {
  const { toast } = useToast();
  const form = useCampaignSettingsForm(campaign);
  const updateMutation = useUpdateCampaign(campaign.id);
  const [showImpact, setShowImpact] = React.useState(false);

  const isLocked = campaign.status === 'completed' || campaign.status === 'cancelled';
  const readOnly = !canManage || isLocked;
  const hasChanges = form.buildPatch() !== null;

  async function submitPatch(): Promise<{ ok: true } | { ok: false; message: string }> {
    const patch = form.buildPatch();
    if (!patch) return { ok: true };
    try {
      await updateMutation.mutateAsync(patch);
      toast('Configuração salva.', 'success');
      return { ok: true };
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Não foi possível salvar agora.';
      return { ok: false, message };
    }
  }

  async function handleSave(): Promise<void> {
    if (!hasChanges) return;
    if (campaign.status === 'active') {
      setShowImpact(true);
      return;
    }
    const result = await submitPatch();
    if (!result.ok) toast(result.message, 'danger');
  }

  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      {isLocked && (
        <div
          className="flex items-start gap-3 px-4 py-3 rounded-sm border"
          style={{
            background: 'var(--surface-muted)',
            borderColor: 'var(--border)',
            borderLeftWidth: 3,
          }}
        >
          <p className="font-sans text-sm text-ink-2">
            Campanha {campaign.status === 'completed' ? 'concluída' : 'cancelada'} — nenhum campo é
            editável.
          </p>
        </div>
      )}

      <fieldset className="flex flex-col gap-6" disabled={readOnly}>
        <SettingsIdentityFieldset
          values={form.values}
          isFieldAllowed={form.isFieldAllowed}
          setField={form.setField}
        />
        <SettingsCadenceFieldset
          values={form.values}
          isFieldAllowed={form.isFieldAllowed}
          setField={form.setField}
        />
      </fieldset>

      <div className="flex flex-col gap-2">
        <p className="font-sans text-xs font-semibold uppercase tracking-wide text-ink-3">
          Prévia de distribuição
        </p>
        <div
          className="rounded-sm border border-border px-4 py-3"
          style={{ background: 'var(--surface-muted)' }}
        >
          <p className="font-sans text-sm text-ink-3">
            Indisponível — o dry-run de distribuição ainda não tem um endpoint publicado para esta
            tela.
          </p>
        </div>
      </div>

      {!readOnly && (
        <div className="flex gap-2.5">
          <Button
            type="button"
            variant="secondary"
            disabled={updateMutation.isPending || !hasChanges}
            onClick={() => void handleSave()}
          >
            {updateMutation.isPending ? 'Salvando…' : 'Salvar alterações'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={updateMutation.isPending || !hasChanges}
            onClick={() => form.resetToServer()}
          >
            Descartar
          </Button>
        </div>
      )}

      {showImpact && (
        <ImpactConfirmDialog
          campaignName={campaign.name}
          changes={form.diffForConfirm()}
          onClose={() => setShowImpact(false)}
          onConfirm={submitPatch}
        />
      )}
    </div>
  );
}
